import React, { useEffect, useState } from 'react';
import { Grid, MenuItem, TextField, Typography, FormControlLabel, Checkbox, Box } from '@mui/material';

import { textFieldHandlerBuilder } from '../../utils/inputHandlers';
import { isStringEmpty } from '../../utils/formValidation';
import FileUpload from '../common/FileUpload';
import NotificationBar from '../common/NotificationBar';
import FileDownload from '../common/FileDownload';
import ProfileSectionController from './ProfileSectionController';
import APICaller, { MethodType } from '../common/APICaller';
import PersonOutlineIcon from '@mui/icons-material/PersonOutline';
import ContactMailIcon from '@mui/icons-material/ContactMail';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import AssignmentIndIcon from '@mui/icons-material/AssignmentInd';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';

export interface BasicInfo {
  id: number | null;
  firstName: string;
  lastName: string;
  email: string;
  city: string;
  province: string;
  country: string;
  timezone: string;
  immigrationStatus?: string;
  lgbtIdentity?: string;
  ageEligibility?: string;
  workEligibility?: string;
  genderIdentity?: string;
  visibleMinority?: string;
  indigenousPerson?: string;
  disability?: string;
  resumeAvailable?: boolean;
  resumeData?: string | null;
  resumeFileName?: string | null;
  transcriptData?: string | null;
  transcriptFileName?: string | null;
  linkedIn?: string | null;
  portfolio?: string | null;
  github?: string | null;
  publishedPapers?: string | null;
  wageSubsidy?: string | null;
  wasCapstone?: boolean | null;
}

interface BasicInfoSectionProp {
  viewMode?: boolean;
  data: BasicInfo;
  resumeAvailable?: boolean;
}

// Canadian time zones (IANA IDs)
const timezones: { value: string; name: string }[] = [
  { value: 'PDT', name: 'Pacific Daylight Time (PDT)' },
  { value: 'MDT', name: 'Mountain Daylight Time (MDT)' },
  { value: 'CDT', name: 'Central Daylight Time (CDT)' },
  { value: 'CST', name: 'Central Standard Time (CST)' },
  { value: 'EDT', name: 'Eastern Daylight Time (EDT)' },
  { value: 'ADT', name: 'Atlantic Daylight Time (ADT)' },
  { value: 'NDT', name: 'Newfoundland Daylight Time (NDT)' },
];

// Immigration status options
const immigrationStatuses: { value: string; name: string }[] = [
  { value: 'Citizen', name: 'Canadian Citizen' },
  { value: 'PR', name: 'Permanent Resident' },
  { value: 'Refugee', name: 'Refugee under the Immigration and Refugee Protection Act' },
  { value: 'Other', name: 'Other' },
];

// LGBT identity options
const lgbtIdentities: { value: string; name: string }[] = [
  { value: 'Yes', name: 'Yes, I identify as LGBTQIA2S+' },
  { value: 'No', name: 'No, I do not identify as LGBTQIA2S+' },
  { value: 'Prefer not to say', name: 'Prefer not to say' },
];

// Age eligibility options for funding programs
const ageEligibilityOptions: { value: string; name: string }[] = [
  { value: 'Yes', name: 'Yes, I am between 15–30 years old' },
  { value: 'No', name: 'No, I am not between 15–30 years old' },
  { value: 'Prefer not to answer', name: 'Prefer not to answer' },
];

// Work eligibility options
const workEligibilityOptions: { value: string; name: string }[] = [
  { value: 'Yes', name: 'Yes' },
  { value: 'No', name: 'No' },
];

// Shared yes/no/prefer-not-to-answer options for new disclosure fields
const yesNoPrefOptions: { value: string; name: string }[] = [
  { value: 'Yes', name: 'Yes' },
  { value: 'No', name: 'No' },
  { value: 'Prefer not to answer', name: 'Prefer not to answer' },
];

// Example list of Canadian provinces
const provinces = [
  { code: 'AB', name: 'Alberta' },
  { code: 'BC', name: 'British Columbia' },
  { code: 'MB', name: 'Manitoba' },
  { code: 'NB', name: 'New Brunswick' },
  { code: 'NL', name: 'Newfoundland and Labrador' },
  { code: 'NS', name: 'Nova Scotia' },
  { code: 'ON', name: 'Ontario' },
  { code: 'PE', name: 'Prince Edward Island' },
  { code: 'QC', name: 'Quebec' },
  { code: 'SK', name: 'Saskatchewan' },
  { code: 'NT', name: 'Northwest Territories' },
  { code: 'NU', name: 'Nunavut' },
  { code: 'YT', name: 'Yukon' },
];

const BasicInfoSection: React.FC<BasicInfoSectionProp> = ({
  viewMode = false,
  data,
  resumeAvailable = true,
}) => {
  const [firstName, setFirstName] = useState<string>('');
  const [lastName, setLastName] = useState<string>('');
  const [city, setCity] = useState<string>('');
  const [province, setProvince] = useState<string>('');
  const [country, setCountry] = useState<string>('');
  const [timezone, setTimezone] = useState<string>('');
  const [immigrationStatus, setImmigrationStatus] = useState<string>('');
  const [lgbtIdentity, setLGBTIdentity] = useState<string>('');
  const [ageEligibility, setAgeEligibility] = useState<string>('');
  const [workEligibility, setWorkEligibility] = useState<string>('');
  const [genderIdentity, setGenderIdentity] = useState<string>('');
  const [visibleMinority, setVisibleMinority] = useState<string>('');
  const [indigenousPerson, setIndigenousPerson] = useState<string>('');
  const [disability, setDisability] = useState<string>('');
  const [linkedIn, setLinkedIn] = useState<string>('');
  const [portfolio, setPortfolio] = useState<string>('');
  const [github, setGithub] = useState<string>('');
  const [publishedPapers, setPublishedPapers] = useState<string>('');
  const [wageSubsidy, setWageSubsidy] = useState<string>('');
  const [wasCapstone, setWasCapstone] = useState<boolean>(false);
  const [applicantID, setApplicantID] = useState<number | null>(null);

  const [isFirstNameEmpty, setIsFirstNameEmpty] = useState<boolean>(false);
  const [isLastNameEmpty, setIsLastNameEmpty] = useState<boolean>(false);
  const [isCityEmpty, setIsCityEmpty] = useState<boolean>(false);
  const [isProvinceEmpty, setIsProvinceEmpty] = useState<boolean>(false);
  const [isCountryEmpty, setIsCountryEmpty] = useState<boolean>(false);
  const [isTimezoneEmpty, setIsTimezoneEmpty] = useState<boolean>(false);
  const [isImmigrationStatusEmpty, setIsImmigrationStatusEmpty] = useState<boolean>(false);
  const [isAgeEligibilityEmpty, setIsAgeEligibilityEmpty] = useState<boolean>(false);
  const [isWorkEligibilityEmpty, setIsWorkEligibilityEmpty] = useState<boolean>(false);
  const [isVisibleMinorityEmpty, setIsVisibleMinorityEmpty] = useState<boolean>(false);
  const [isIndigenousPersonEmpty, setIsIndigenousPersonEmpty] = useState<boolean>(false);
  const [isDisabilityEmpty, setIsDisabilityEmpty] = useState<boolean>(false);

  const [showNotification, setShowNotification] = useState(false);

  useEffect(() => {
    setFirstName(data.firstName || '');
    setLastName(data.lastName || '');
    setCity(data.city || '');
    setProvince(data.province || '');
    setCountry(data.country || '');
    setTimezone(data.timezone || '');
    setImmigrationStatus(data.immigrationStatus || '');
    setLGBTIdentity(data.lgbtIdentity || '');
    setAgeEligibility(data.ageEligibility || '');
    setWorkEligibility(data.workEligibility || '');
    setGenderIdentity(data.genderIdentity || '');
    setVisibleMinority(data.visibleMinority || '');
    setIndigenousPerson(data.indigenousPerson || '');
    setDisability(data.disability || '');
    setLinkedIn(data.linkedIn || '');
    setPortfolio(data.portfolio || '');
    setGithub(data.github || '');
    setPublishedPapers(data.publishedPapers || '');
    setWageSubsidy(data.wageSubsidy || '');
    setWasCapstone(data.wasCapstone || false);
    setApplicantID(data.id || null);
  }, [data]);

  const saveButtonHandler = () => {
    const firstNameFieldEmpty = isStringEmpty(firstName);
    const lastNameFieldEmpty = isStringEmpty(lastName);
    const cityFieldEmpty = isStringEmpty(city);
    const provinceFieldEmpty = isStringEmpty(province);
    const countryFieldEmpty = isStringEmpty(country);
    const timezoneFieldEmpty = isStringEmpty(timezone);
    const immigrationStatusFieldEmpty = isStringEmpty(immigrationStatus);
    const ageEligibilityFieldEmpty = isStringEmpty(ageEligibility);
    const workEligibilityFieldEmpty = isStringEmpty(workEligibility);
    const visibleMinorityFieldEmpty = isStringEmpty(visibleMinority);
    const indigenousPersonFieldEmpty = isStringEmpty(indigenousPerson);
    const disabilityFieldEmpty = isStringEmpty(disability);

    if (
      !firstNameFieldEmpty &&
      !lastNameFieldEmpty &&
      !cityFieldEmpty &&
      !provinceFieldEmpty &&
      !countryFieldEmpty &&
      !timezoneFieldEmpty &&
      !immigrationStatusFieldEmpty &&
      !ageEligibilityFieldEmpty &&
      !workEligibilityFieldEmpty &&
      !visibleMinorityFieldEmpty &&
      !indigenousPersonFieldEmpty &&
      !disabilityFieldEmpty
    ) {
      const updateData = {
        applicant: {
          firstName,
          lastName,
          province,
          city,
          country,
          timezone,
          immigrationStatus,
          lgbtIdentity,
          ageEligibility,
          workEligibility,
          genderIdentity,
          visibleMinority,
          indigenousPerson,
          disability,
          email: data.email, // Include email in the update payload
          linkedIn,
          portfolio,
          github,
          publishedPapers,
          wageSubsidy,
          wasCapstone,
        },
      };
      const route = `/profile/applicant/${applicantID}/update`;
      const method = MethodType.PUT;
      APICaller(route, method, updateData).then(res => {
        if (res.errorMessage) {
          alert(res.errorMessage);
          return;
        }
        if (res.applicantId) {
          setShowNotification(true);
        }
      });
    }
    setIsFirstNameEmpty(firstNameFieldEmpty);
    setIsLastNameEmpty(lastNameFieldEmpty);
    setIsCityEmpty(cityFieldEmpty);
    setIsProvinceEmpty(provinceFieldEmpty);
    setIsCountryEmpty(countryFieldEmpty);
    setIsTimezoneEmpty(timezoneFieldEmpty);
    setIsImmigrationStatusEmpty(immigrationStatusFieldEmpty);
    setIsAgeEligibilityEmpty(ageEligibilityFieldEmpty);
    setIsWorkEligibilityEmpty(workEligibilityFieldEmpty);
    setIsVisibleMinorityEmpty(visibleMinorityFieldEmpty);
    setIsIndigenousPersonEmpty(indigenousPersonFieldEmpty);
    setIsDisabilityEmpty(disabilityFieldEmpty);
  };

  const fileChangeHandler = (file: File | null) => {
    if (file && applicantID) {
      const formData = new FormData();
      formData.append('file', file);
      APICaller(
        `/profile/resume/${applicantID}/upload`,
        MethodType.POST,
        formData,
        true,
      ).then(res => {
        if (!res.errorMessage) {
          setShowNotification(true);
        }
      });
    }
  };

  const transcriptChangeHandler = (file: File | null) => {
    if (file && applicantID) {
      const formData = new FormData();
      formData.append('file', file);
      APICaller(
        `/profile/transcript/${applicantID}/upload`,
        MethodType.POST,
        formData,
        true,
      ).then(res => {
        if (!res.errorMessage) {
          setShowNotification(true);
        }
      });
    }
  };

  return (
    <Grid
      item
      container
      direction="row"
      alignItems="center"
      spacing={2}
      justifyItems="start"
    >
      {!viewMode && (
        <>
          <FileUpload
            fileUploadHandler={fileChangeHandler}
            buttonText="Resume Upload"
            enabled={true}
            oldFilePath={data.resumeFileName || ''}
          />
          <FileUpload
            fileUploadHandler={transcriptChangeHandler}
            buttonText="Transcript Upload"
            enabled={true}
            oldFilePath={data.transcriptFileName || ''}
          />
        </>
      )}
      {viewMode && (
        <>
          <FileDownload
            url={`/profile/review/applicants/${data.id}/resume/download`}
            fileAvailable={resumeAvailable}
            displayedText={
              resumeAvailable ? 'Download Resume' : 'No Resume Available'
            }
          />
          <FileDownload
            url={`/profile/review/applicants/${data.id}/transcript/download`}
            fileAvailable={!!data.transcriptFileName}
            displayedText={
              data.transcriptFileName ? 'Download Transcript' : 'No Transcript Available'
            }
          />
        </>
      )}
      <Grid item container spacing={3} xs={12} md={12}>
        <Grid item xs={12} md={12} sx={{ display: 'flex', alignItems: 'center', marginTop: '3vh', marginBottom: '0.5vh' }}>
          <PersonOutlineIcon sx={{ color: 'var(--ttg-green)', marginRight: '1rem', fontSize: '2.5rem' }} />
          <Typography
            variant="h4"
            sx={{
              color: 'var(--ttg-black)',
              fontFamily: 'inherit',
              textAlign: 'left',
              fontSize: { xs: '1.5rem', sm: '2.125rem' },
              fontWeight: 600,
              textTransform: 'uppercase',
              letterSpacing: '0.1rem'
            }}
          >
            Contact Person
          </Typography>
        </Grid>
        <Grid item xs={12} sm={6} md={6}>
          <TextField
            margin="normal"
            required
            fullWidth
            name="first_name"
            label="First Name"
            type="text"
            id="first_name"
            value={firstName}
            onChange={textFieldHandlerBuilder(setFirstName)}
            error={isFirstNameEmpty}
            InputProps={{ sx: { borderRadius: '12px', padding: '4px 2px', backgroundColor: '#f8f9fb' } }}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={6}>
          <TextField
            margin="normal"
            required
            fullWidth
            name="last_name"
            label="Last Name"
            type="text"
            id="last_name"
            value={lastName}
            onChange={textFieldHandlerBuilder(setLastName)}
            error={isLastNameEmpty}
            InputProps={{ sx: { borderRadius: '12px', padding: '4px 2px', backgroundColor: '#f8f9fb' } }}
          />
        </Grid>

        <Grid item xs={12} md={12} sx={{ display: 'flex', alignItems: 'center', marginTop: '1.5vh', marginBottom: '0.5vh' }}>
          <ContactMailIcon sx={{ color: 'var(--ttg-green)', marginRight: '1rem', fontSize: '2rem' }} />
          <Typography
            variant="h6"
            sx={{
              color: 'var(--ttg-black)',
              fontFamily: 'inherit',
              textAlign: 'left',
              fontWeight: 600,
              textTransform: 'uppercase',
              letterSpacing: '0.05rem'
            }}
          >
            Contact Info
          </Typography>
        </Grid>

        <Grid item xs={12} sm={6} md={6}>
          <TextField
            required
            fullWidth
            id="email"
            label="Email Address"
            name="email"
            type="email"
            value={data.email}
            disabled={true}
            InputProps={{ sx: { borderRadius: '12px', padding: '4px 2px', backgroundColor: '#f8f9fb' } }}
          />
        </Grid>
        <Grid item xs={12} md={12} sx={{ display: 'flex', alignItems: 'center', marginTop: '1.5vh', marginBottom: '0.5vh' }}>
          <LocationOnIcon sx={{ color: 'var(--ttg-green)', marginRight: '1rem', fontSize: '2rem' }} />
          <Typography
            variant="h6"
            sx={{
              color: 'var(--ttg-black)',
              fontFamily: 'inherit',
              textAlign: 'left',
              fontWeight: 600,
              textTransform: 'uppercase',
              letterSpacing: '0.05rem'
            }}
          >
            Location
          </Typography>
        </Grid>

        <Grid item xs={12} sm={6} md={6}>
          <TextField
            required
            fullWidth
            name="city"
            label="City"
            type="text"
            id="city"
            value={city}
            onChange={textFieldHandlerBuilder(setCity)}
            error={isCityEmpty}
            InputProps={{ sx: { borderRadius: '12px', padding: '4px 2px', backgroundColor: '#f8f9fb' } }}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={6}>
          <TextField
            required
            select
            fullWidth
            name="province"
            label="Province"
            id="province"
            value={province}
            onChange={e => setProvince(e.target.value)}
            error={isProvinceEmpty}
            helperText={isProvinceEmpty ? 'Please select a province.' : ''}
            InputProps={{ sx: { borderRadius: '12px', padding: '4px 2px', backgroundColor: '#f8f9fb' } }}
            SelectProps={{
              MenuProps: {
                PaperProps: {
                  style: {
                    maxHeight: 48 * 4.5 + 8,
                    width: 320,
                  },
                },
              },
            }}
          >
            {provinces.map(prov => (
              <MenuItem key={prov.code} value={prov.code}>
                {prov.name}
              </MenuItem>
            ))}
          </TextField>
        </Grid>
        <Grid item xs={12} sm={6} md={6}>
          <TextField
            margin="normal"
            required
            fullWidth
            name="country"
            label="Country"
            type="text"
            id="country"
            value={country}
            onChange={textFieldHandlerBuilder(setCountry)}
            error={isCountryEmpty}
            InputProps={{ sx: { borderRadius: '12px', padding: '4px 2px', backgroundColor: '#f8f9fb' } }}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={6}>
          <TextField
            margin="normal"
            required
            select
            fullWidth
            name="timezone"
            label="Time Zone"
            id="timezone"
            value={timezone}
            onChange={e => setTimezone(e.target.value)}
            error={isTimezoneEmpty}
            helperText={isTimezoneEmpty ? 'Please select a time zone.' : ''}
            InputProps={{ sx: { borderRadius: '12px', padding: '4px 2px', backgroundColor: '#f8f9fb' } }}
            SelectProps={{
              MenuProps: {
                PaperProps: {
                  style: {
                    maxHeight: 48 * 4.5 + 8,
                    width: 320,
                  },
                },
              },
            }}
          >
            {timezones.map(tz => (
              <MenuItem key={tz.value} value={tz.value}>
                {tz.name}
              </MenuItem>
            ))}
          </TextField>
        </Grid>

        <Grid item xs={12} md={12} sx={{ display: 'flex', alignItems: 'center', marginTop: '1.5vh', marginBottom: '0.5vh' }}>
          <InfoOutlinedIcon sx={{ color: 'var(--ttg-green)', marginRight: '1rem', fontSize: '2rem' }} />
          <Typography
            variant="h6"
            sx={{
              color: 'var(--ttg-black)',
              fontFamily: 'inherit',
              textAlign: 'left',
              fontWeight: 600,
              textTransform: 'uppercase',
              letterSpacing: '0.05rem'
            }}
          >
            Links (Optional)
          </Typography>
        </Grid>

        <Grid item xs={12} sm={6} md={6}>
          <TextField
            fullWidth
            name="linkedin"
            label="LinkedIn Profile"
            type="text"
            id="linkedin"
            value={linkedIn}
            onChange={textFieldHandlerBuilder(setLinkedIn)}
            InputProps={{ sx: { borderRadius: '12px', padding: '4px 2px', backgroundColor: '#f8f9fb' } }}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={6}>
          <TextField
            fullWidth
            name="portfolio"
            label="Portfolio/Personal Website"
            type="text"
            id="portfolio"
            value={portfolio}
            onChange={textFieldHandlerBuilder(setPortfolio)}
            InputProps={{ sx: { borderRadius: '12px', padding: '4px 2px', backgroundColor: '#f8f9fb' } }}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={6}>
          <TextField
            fullWidth
            name="github"
            label="GitHub/Bitbucket"
            type="text"
            id="github"
            value={github}
            onChange={textFieldHandlerBuilder(setGithub)}
            InputProps={{ sx: { borderRadius: '12px', padding: '4px 2px', backgroundColor: '#f8f9fb' } }}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={6}>
          <TextField
            fullWidth
            name="published_papers"
            label="Published Papers/Other Links"
            type="text"
            id="published_papers"
            value={publishedPapers}
            onChange={textFieldHandlerBuilder(setPublishedPapers)}
            InputProps={{ sx: { borderRadius: '12px', padding: '4px 2px', backgroundColor: '#f8f9fb' } }}
          />
        </Grid>

        <Grid item xs={12} md={12} sx={{ display: 'flex', alignItems: 'center', marginTop: '1.5vh', marginBottom: '0.5vh' }}>
          <AssignmentIndIcon sx={{ color: 'var(--ttg-green)', marginRight: '1rem', fontSize: '2rem' }} />
          <Typography
            variant="h6"
            sx={{
              color: 'var(--ttg-black)',
              fontFamily: 'inherit',
              textAlign: 'left',
              fontWeight: 600,
              textTransform: 'uppercase',
              letterSpacing: '0.05rem'
            }}
          >
            Residency & Eligibility
          </Typography>
        </Grid>

        <Grid item xs={12} sm={6} md={6}>
          <TextField
            required
            select
            fullWidth
            name="immigration_status"
            label="Canadian Residency Status"
            id="immigration_status"
            value={immigrationStatus}
            onChange={e => setImmigrationStatus(e.target.value)}
            error={isImmigrationStatusEmpty}
            helperText={
              isImmigrationStatusEmpty
                ? 'Please select your immigration status.'
                : ''
            }
            InputProps={{ sx: { borderRadius: '12px', padding: '4px 2px', backgroundColor: '#f8f9fb' } }}
            SelectProps={{
              MenuProps: {
                PaperProps: {
                  style: {
                    maxHeight: 48 * 4.5 + 8,
                    width: 480,
                  },
                },
              },
            }}
          >
            {immigrationStatuses.map(status => (
              <MenuItem key={status.value} value={status.value}>
                {status.name}
              </MenuItem>
            ))}
          </TextField>
        </Grid>

        <Grid item xs={12} sm={6} md={6}>
          <Typography fontWeight={'bold'} textAlign={'left'} variant='body2' sx={{ marginBottom: '2vh', fontSize: { xs: '0.8rem', sm: '0.875rem' } }}>
            Are you legally able to work in Canada?
          </Typography>
          <TextField
            required
            select
            fullWidth
            name="work_eligibility"
            label="Work Eligibility"
            id="work_eligibility"
            value={workEligibility}
            onChange={e => setWorkEligibility(e.target.value)}
            error={isWorkEligibilityEmpty}
            helperText={
              isWorkEligibilityEmpty
                ? 'Please select your work eligibility.'
                : ''
            }
            InputProps={{ sx: { borderRadius: '12px', padding: '4px 2px', backgroundColor: '#f8f9fb' } }}
            SelectProps={{
              MenuProps: {
                PaperProps: {
                  style: {
                    maxHeight: 48 * 4.5 + 8,
                    width: 480,
                  },
                },
              },
            }}
          >
            {workEligibilityOptions.map(option => (
              <MenuItem key={option.value} value={option.value}>
                {option.name}
              </MenuItem>
            ))}
          </TextField>
        </Grid>

        <Grid item xs={12} md={12} sx={{ display: 'flex', alignItems: 'center', marginTop: '3vh', marginBottom: '0.5vh' }}>
          <InfoOutlinedIcon sx={{ color: 'var(--ttg-green)', marginRight: '1rem', fontSize: '2.5rem' }} />
          <Typography
            variant="h4"
            sx={{
              color: 'var(--ttg-black)',
              fontFamily: 'inherit',
              textAlign: 'left',
              fontSize: { xs: '1.5rem', sm: '2.125rem' },
              fontWeight: 600,
              textTransform: 'uppercase',
              letterSpacing: '0.1rem'
            }}
          >
            Voluntary Self Disclosure
          </Typography>
        </Grid>
        <Grid item xs={12} md={12}>
          <Typography textAlign={'left'} variant='body1' sx={{ marginBottom: '1vh' }}>
            We collect this information solely for the purpose of meeting eligibility criteria for government grants and workforce equity programs. Your response is confidential and will not affect your application outcome. For more information, please see the
            <a href="https://albertahumanrights.ab.ca/" target="_blank" rel="noopener noreferrer"> Alberta Human Rights Commission.</a>
          </Typography>
        </Grid>

        <Grid item xs={12} sm={6} md={6}>
          <Typography fontWeight={'bold'} textAlign={'left'} variant='body2' sx={{ marginBottom: '2vh', fontSize: { xs: '0.8rem', sm: '0.875rem' } }}>
            Do you fall within 15–30 years old age range?
          </Typography>
          <TextField
            required
            select
            fullWidth
            name="age_eligibility"
            label="Eligibility for Funding Programs"
            id="age_eligibility"
            value={ageEligibility}
            onChange={e => setAgeEligibility(e.target.value)}
            error={isAgeEligibilityEmpty}
            helperText={
              isAgeEligibilityEmpty
                ? 'Please indicate your age eligibility.'
                : ''
            }
            InputProps={{ sx: { borderRadius: '12px', padding: '4px 2px', backgroundColor: '#f8f9fb' } }}
            SelectProps={{
              MenuProps: {
                PaperProps: {
                  style: {
                    maxHeight: 48 * 4.5 + 8,
                    width: 480,
                  },
                },
              },
            }}
          >
            {ageEligibilityOptions.map(option => (
              <MenuItem key={option.value} value={option.value}>
                {option.name}
              </MenuItem>
            ))}
          </TextField>
        </Grid>


        <Grid item xs={12} sm={6} md={6}>
          <Typography fontWeight={'bold'} textAlign={'left'} variant='body2' sx={{ marginBottom: '2vh', fontSize: { xs: '0.8rem', sm: '0.875rem' } }}>
            Do you identify yourself as LGBTQIA2S+?
          </Typography>
          <TextField
            select
            fullWidth
            name="LGBTQ2S+"
            label="LGBTQIA2S+ Identity"
            id="LGBT"
            value={lgbtIdentity}
            onChange={e => setLGBTIdentity(e.target.value)}
            InputProps={{ sx: { borderRadius: '12px', padding: '4px 2px', backgroundColor: '#f8f9fb' } }}
            SelectProps={{
              MenuProps: {
                PaperProps: {
                  style: {
                    maxHeight: 48 * 4.5 + 8,
                    width: 480,
                  },
                },
              },
            }}
          >
            {lgbtIdentities.map(identity => (
              <MenuItem key={identity.value} value={identity.value}>
                {identity.name}
              </MenuItem>
            ))}
          </TextField>
        </Grid>

        <Grid item xs={12} sm={6} md={6}>
          <Typography fontWeight={'bold'} textAlign={'left'} variant='body2' sx={{ marginBottom: '2vh', fontSize: { xs: '0.8rem', sm: '0.875rem' } }}>
            What is your gender identity?
          </Typography>
          <TextField
            select
            fullWidth
            name="gender_identity"
            label="Gender Identity"
            id="gender_identity"
            value={genderIdentity}
            onChange={e => setGenderIdentity(e.target.value)}
            InputProps={{ sx: { borderRadius: '12px', padding: '4px 2px', backgroundColor: '#f8f9fb' } }}
            SelectProps={{ MenuProps: { PaperProps: { style: { maxHeight: 48 * 4.5 + 8, width: 480 } } } }}
          >
            {[
              { value: 'Man', name: 'Man' },
              { value: 'Woman', name: 'Woman' },
              { value: 'Non-binary', name: 'Non-binary' },
              { value: 'Two-Spirit', name: 'Two-Spirit' },
              { value: 'Other', name: 'Other' },
              { value: 'Prefer not to answer', name: 'Prefer not to answer' },
            ].map(option => (
              <MenuItem key={option.value} value={option.value}>
                {option.name}
              </MenuItem>
            ))}
          </TextField>
        </Grid>

        <Grid item xs={12} sm={6} md={6}>
          <Typography fontWeight={'bold'} textAlign={'left'} variant='body2' sx={{ marginBottom: '2vh', fontSize: { xs: '0.8rem', sm: '0.875rem' } }}>
            Do you identify as a member of a visible minority?
          </Typography>
          <TextField
            required
            select
            fullWidth
            name="visible_minority"
            label="Visible Minority"
            id="visible_minority"
            value={visibleMinority}
            onChange={e => setVisibleMinority(e.target.value)}
            error={isVisibleMinorityEmpty}
            helperText={isVisibleMinorityEmpty ? 'Please select an option.' : ''}
            InputProps={{ sx: { borderRadius: '12px', padding: '4px 2px', backgroundColor: '#f8f9fb' } }}
            SelectProps={{ MenuProps: { PaperProps: { style: { maxHeight: 48 * 4.5 + 8, width: 480 } } } }}
          >
            {yesNoPrefOptions.map(option => (
              <MenuItem key={option.value} value={option.value}>
                {option.name}
              </MenuItem>
            ))}
          </TextField>
        </Grid>

        <Grid item xs={12} sm={6} md={6}>
          <Typography fontWeight={'bold'} textAlign={'left'} variant='body2' sx={{ marginBottom: '2vh', fontSize: { xs: '0.8rem', sm: '0.875rem' } }}>
            Do you identify as an Indigenous person?
          </Typography>
          <TextField
            required
            select
            fullWidth
            name="indigenous_person"
            label="Indigenous Person"
            id="indigenous_person"
            value={indigenousPerson}
            onChange={e => setIndigenousPerson(e.target.value)}
            error={isIndigenousPersonEmpty}
            helperText={isIndigenousPersonEmpty ? 'Please select an option.' : ''}
            InputProps={{ sx: { borderRadius: '12px', padding: '4px 2px', backgroundColor: '#f8f9fb' } }}
            SelectProps={{ MenuProps: { PaperProps: { style: { maxHeight: 48 * 4.5 + 8, width: 480 } } } }}
          >
            {yesNoPrefOptions.map(option => (
              <MenuItem key={option.value} value={option.value}>
                {option.name}
              </MenuItem>
            ))}
          </TextField>
        </Grid>

        <Grid item xs={12} sm={6} md={6}>
          <Typography fontWeight={'bold'} textAlign={'left'} variant='body2' sx={{ marginBottom: '2vh', fontSize: { xs: '0.8rem', sm: '0.875rem' } }}>
            Do you identify as a person with a disability?
          </Typography>
          <TextField
            required
            select
            fullWidth
            name="disability"
            label="Disability"
            id="disability"
            value={disability}
            onChange={e => setDisability(e.target.value)}
            error={isDisabilityEmpty}
            helperText={isDisabilityEmpty ? 'Please select an option.' : ''}
            InputProps={{ sx: { borderRadius: '12px', padding: '4px 2px', backgroundColor: '#f8f9fb' } }}
            SelectProps={{ MenuProps: { PaperProps: { style: { maxHeight: 48 * 4.5 + 8, width: 480 } } } }}
          >
            {yesNoPrefOptions.map(option => (
              <MenuItem key={option.value} value={option.value}>
                {option.name}
              </MenuItem>
            ))}
          </TextField>
        </Grid>
      </Grid>

      <Grid item xs={12} md={12} sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginTop: '3vh', marginBottom: '0.5vh' }}>
        <Box sx={{ display: 'flex', alignItems: 'center' }}>
          <AssignmentIndIcon sx={{ color: 'var(--ttg-green)', marginRight: '1rem', fontSize: '2.5rem' }} />
          <Typography
            variant="h4"
            sx={{
              color: 'var(--ttg-black)',
              fontFamily: 'inherit',
              textAlign: 'left',
              fontSize: { xs: '1.5rem', sm: '2.125rem' },
              fontWeight: 600,
              textTransform: 'uppercase',
              letterSpacing: '0.1rem'
            }}
          >
            Questions
          </Typography>
        </Box>
        {!viewMode && (
          <FormControlLabel
            sx={{ mr: 0, whiteSpace: 'nowrap' }}
            control={
              <Checkbox
                id="was_capstone"
                checked={wasCapstone}
                onChange={e => setWasCapstone(e.target.checked)}
                sx={{ color: 'var(--ttg-green)', '&.Mui-checked': { color: 'var(--ttg-green)' } }}
              />
            }
            label={
              <Typography variant="body2" fontWeight={600} sx={{ fontSize: { xs: '0.8rem', sm: '0.875rem' } }}>
                Were you Capstone?
              </Typography>
            }
          />
        )}
        {viewMode && wasCapstone && (
          <Typography
            variant="body2"
            sx={{
              px: 1.5,
              py: 0.4,
              borderRadius: 1,
              backgroundColor: 'var(--ttg-green)',
              color: 'white',
              fontWeight: 700,
              fontSize: '0.75rem',
              whiteSpace: 'nowrap',
            }}
          >
            Capstone
          </Typography>
        )}
      </Grid>

      <Grid item xs={12} sm={6} md={6}>
        <Typography fontWeight={'bold'} textAlign={'left'} variant='body2' sx={{ marginBottom: '2vh', fontSize: { xs: '0.8rem', sm: '0.875rem' } }}>
          Are you eligible for any wage subsidy programs? (e.g., Canada Summer Jobs, Student Work Placement Program, etc.) If yes, please specify.
        </Typography>
        <TextField
          fullWidth
          multiline
          rows={3}
          name="wage_subsidy"
          label="Wage Subsidy Information"
          id="wage_subsidy"
          value={wageSubsidy}
          onChange={textFieldHandlerBuilder(setWageSubsidy)}
          InputProps={{ sx: { borderRadius: '12px', padding: '10px', backgroundColor: '#f8f9fb' } }}
        />
      </Grid>
      {!viewMode && (
        <>
          <ProfileSectionController
            deletable={false}
            saveButtonHandler={saveButtonHandler}
          />
          <NotificationBar
            open={showNotification}
            setOpen={setShowNotification}
            title="Successs!"
            message="We have your personal info on record!"
            hideDuraction={2000}
          />
        </>
      )}
    </Grid>
  );
};

export default BasicInfoSection;
